import * as React from 'react';
import { Button, Transfer } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import './StackedTransferSelector.css';

type TransferOption = {
    key: string;
    title: string;
    description?: string;
    disabled?: boolean;
};

interface StackedTransferSelectorProps {
    items: TransferOption[];
    targetKeys: string[];
    onChange: (nextKeys: string[]) => void;
    availableTitle: string;
    selectedTitle: string;
    searchPlaceholder?: string;
    height?: number;
}

const MIN_SECTION_HEIGHT = 120;
const MAX_SECTION_HEIGHT = 480;

type ResizeSection = 'selected' | 'available';

function clampSectionHeight(value: number): number {
    return Math.max(MIN_SECTION_HEIGHT, Math.min(MAX_SECTION_HEIGHT, value));
}

export const StackedTransferSelector: React.FC<StackedTransferSelectorProps> = ({
    items,
    targetKeys,
    onChange,
    availableTitle,
    selectedTitle,
    searchPlaceholder = 'Search by name...',
    height = 200
}) => {
    const [selectedKeys, setSelectedKeys] = React.useState<string[]>([]);
    const [selectedHeight, setSelectedHeight] = React.useState(height);
    const [availableHeight, setAvailableHeight] = React.useState(height);
    const [isResizing, setIsResizing] = React.useState(false);
    const resizeStateRef = React.useRef<{
        section: ResizeSection;
        startY: number;
        startHeight: number;
    } | null>(null);

    React.useEffect(() => {
        const validKeys = new Set(items.map(item => item.key));
        setSelectedKeys(current => current.filter(key => validKeys.has(key)));
    }, [items]);

    React.useEffect(() => {
        setSelectedHeight(height);
        setAvailableHeight(height);
    }, [height]);

    React.useEffect(() => {
        if (!isResizing) return;

        const onMove = (event: MouseEvent) => {
            const state = resizeStateRef.current;
            if (!state) return;

            const nextHeight = clampSectionHeight(state.startHeight + (event.clientY - state.startY));
            if (state.section === 'selected') {
                setSelectedHeight(nextHeight);
            } else {
                setAvailableHeight(nextHeight);
            }
        };

        const onUp = () => {
            resizeStateRef.current = null;
            setIsResizing(false);
        };

        document.body.style.cursor = 'row-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);

        return () => {
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
    }, [isResizing]);

    const beginResize = (section: ResizeSection, startHeight: number) => (event: React.MouseEvent) => {
        if (event.button !== 0) return;
        event.preventDefault();
        resizeStateRef.current = {
            section,
            startY: event.clientY,
            startHeight
        };
        setIsResizing(true);
    };

    const setSectionHeight = (section: ResizeSection, nextHeight: number) => {
        const clamped = clampSectionHeight(nextHeight);
        if (section === 'selected') {
            setSelectedHeight(clamped);
        } else {
            setAvailableHeight(clamped);
        }
    };

    const onResizeKeyDown = (section: ResizeSection) => (event: React.KeyboardEvent) => {
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setSectionHeight(section, (section === 'selected' ? selectedHeight : availableHeight) - 16);
        } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            setSectionHeight(section, (section === 'selected' ? selectedHeight : availableHeight) + 16);
        } else if (event.key === 'Home') {
            event.preventDefault();
            setSectionHeight(section, MIN_SECTION_HEIGHT);
        } else if (event.key === 'End') {
            event.preventDefault();
            setSectionHeight(section, MAX_SECTION_HEIGHT);
        }
    };

    return (
        <div
            className={`stacked-transfer-selector${isResizing ? ' stacked-transfer-selector--resizing' : ''}`}
            style={{ width: '100%' }}
        >
            <Transfer
                dataSource={items}
                titles={['', '']}
                targetKeys={targetKeys}
                selectedKeys={selectedKeys}
                onChange={nextTargetKeys => onChange(nextTargetKeys as string[])}
                onSelectChange={(sourceSelectedKeys, targetSelectedKeys) =>
                    setSelectedKeys([
                        ...(sourceSelectedKeys as string[]),
                        ...(targetSelectedKeys as string[])
                    ])
                }
                render={item => (
                    <span className="stacked-transfer-item">
                        <span className="stacked-transfer-item-title">{item.title}</span>
                        {targetKeys.includes(item.key) && item.description && (
                            <span className="stacked-transfer-item-hint">{item.description}</span>
                        )}
                    </span>
                )}
                showSearch
                filterOption={(inputValue, item) =>
                    item.title.toLocaleLowerCase().includes(inputValue.trim().toLocaleLowerCase())
                    || (item.description || '').toLocaleLowerCase().includes(inputValue.trim().toLocaleLowerCase())
                }
                selectAllLabels={[
                    ({ totalCount }) => `${availableTitle} (${totalCount})`,
                    ({ totalCount }) => `${selectedTitle} (${totalCount})`,
                ]}
                actions={[
                    <Button
                        key="add"
                        type="primary"
                        size="small"
                        icon={<UpOutlined />}
                        aria-label={`Move to ${selectedTitle}`}
                    />,
                    <Button
                        key="remove"
                        size="small"
                        icon={<DownOutlined />}
                        aria-label={`Move to ${availableTitle}`}
                    />,
                ]}
                footer={(_, info) => {
                    const isSelectedSection = info?.direction === 'right';
                    const section: ResizeSection = isSelectedSection ? 'selected' : 'available';
                    const sectionHeight = isSelectedSection ? selectedHeight : availableHeight;
                    const label = isSelectedSection ? selectedTitle : availableTitle;

                    return (
                        <div
                            role="separator"
                            aria-orientation="horizontal"
                            aria-label={`Resize ${label}`}
                            aria-valuemin={MIN_SECTION_HEIGHT}
                            aria-valuemax={MAX_SECTION_HEIGHT}
                            aria-valuenow={Math.round(sectionHeight)}
                            tabIndex={0}
                            className="stacked-transfer-resize-handle"
                            onMouseDown={beginResize(section, sectionHeight)}
                            onKeyDown={onResizeKeyDown(section)}
                        />
                    );
                }}
                styles={{
                    source: {
                        section: {
                            width: '100%',
                            height: availableHeight
                        }
                    },
                    target: {
                        section: {
                            width: '100%',
                            height: selectedHeight
                        }
                    },
                    actions: {
                        margin: 0,
                        alignSelf: 'center'
                    }
                }}
                style={{
                    display: 'flex',
                    flexDirection: 'column-reverse',
                    gap: 12,
                    width: '100%'
                }}
                locale={{
                    itemUnit: 'item',
                    itemsUnit: 'items',
                    searchPlaceholder,
                    notFoundContent: 'No items found'
                }}
            />
        </div>
    );
};